/**
 * Maps oncology / STP table rows into Appeal Info modal data.
 */

import { CaseAppealsTableRowData } from "../../../data-model/caseAppealsTable";
import { OncologySubmissionTableDataModel } from "../../../data-model/tablesData";
import { OrderStatus } from "../../../enums/authStatus";
import { BadgeColor, BadgeTextColor } from "../../../enums/coloredText";
import { CaseAppealsTableColumnKeys } from "../../../enums/tableColumnKeys";

/**
 * Builds a Case Appeals–shaped row for Appeal Info modal from an oncology
 * submission or STP table row (same modal as Case Appeals Edit).
 *
 * Forwards `appeal_poc` when the source row has one so save does not wipe it.
 *
 * @param rowData - Formatted delivery / STP table row with appeal fields
 * @returns Row compatible with the Case Appeals Appeal Info Edit modal
 */
export const mapRowToAppealInfoModalData = (
  rowData: OncologySubmissionTableDataModel,
): CaseAppealsTableRowData => {
  const record = rowData as OncologySubmissionTableDataModel & {
    appeal_comment?: string | null;
    appeal_sent_at?: string | null;
    appeal_poc?: string | null;
    patient_mrn?: string | null;
  };

  const patientName = String(record.patient_name ?? "").trim() || null;
  const patientMrn = String(record.patient_mrn ?? "").trim() || null;
  const dob = String(record.dob ?? "").trim() || null;
  const drug = String(record.drug ?? "").trim() || null;
  const responseStatus = String(record.response_status ?? "").trim() || null;
  const appealType = String(record.appeal_type ?? "").trim() || null;
  const appealStatus = String(record.appeal_status ?? "").trim() || null;
  const appealReason = String(record.appeal_reason ?? "").trim() || null;
  const appealComment =
    String(record.appeal_comment ?? record.appeal_comments ?? "").trim() ||
    null;
  const appealPoc = String(record.appeal_poc ?? "").trim() || null;
  const dumpedAt = String(record.dumped_at ?? "").trim() || null;
  const appealSentAt = String(record.appeal_sent_at ?? "").trim() || null;

  const emptyBadge = {
    text: "--",
    color: BadgeTextColor[OrderStatus.NO_STATUS],
    bgColor: BadgeColor[OrderStatus.NO_STATUS],
    hoverText: "--",
  };

  return {
    identifier: String(record.identifier ?? "").trim(),
    appeal_id: null,
    patient_name: patientName,
    patient_mrn: patientMrn,
    dob,
    drug,
    covermymed_id: String(record.covermymed_id ?? "").trim() || null,
    second_stp_status: String(record.second_stp_status ?? "").trim() || null,
    response_status: responseStatus,
    appeal_type: appealType,
    appeal_status: appealStatus,
    appeal_reason: appealReason,
    appeal_comment: appealComment,
    appeal_poc: appealPoc,
    appeal_probability: null,
    appeal_band: null,
    appeal_sent_at: appealSentAt,
    appeal_updated_at: null,
    appeal_case_id: null,
    appeal_follow_up_date: null,
    filename: String(record.filename ?? "").trim() || null,
    dumped_at: dumpedAt,
    // Only the appeal list API joins the denial summary; submission rows have none.
    pa_outcome_provider_summary:
      String(record.pa_outcome_provider_summary ?? "").trim() || null,
    [CaseAppealsTableColumnKeys.DATE_OF_WORK]: {
      mainText: "--",
      secondaryText: "Batch : --",
      hideCopyIcon: true,
    },
    [CaseAppealsTableColumnKeys.PATIENT_NAME]: {
      mainText: patientName ?? "--",
      secondaryText: `MRN : ${patientMrn ?? "--"}`,
      hideCopyIcon: true,
    },
    [CaseAppealsTableColumnKeys.DOB]: dob ?? "--",
    [CaseAppealsTableColumnKeys.DRUG]: drug ?? "--",
    [CaseAppealsTableColumnKeys.COVER_MY_MEDS_ID]:
      String(record.covermymed_id ?? "").trim() || "--",
    [CaseAppealsTableColumnKeys.SECOND_STP_STATUS]: emptyBadge,
    [CaseAppealsTableColumnKeys.RESPONSE_STATUS]: emptyBadge,
    [CaseAppealsTableColumnKeys.CASE_ID]: "",
    [CaseAppealsTableColumnKeys.POC]: appealPoc ?? "",
    [CaseAppealsTableColumnKeys.CASE_STATUS]: emptyBadge,
    [CaseAppealsTableColumnKeys.APPEAL_PROBABILITY]: emptyBadge,
    [CaseAppealsTableColumnKeys.ACTION]: {
      value: "Add",
      isDisabled: false,
      title: "Edit",
    },
    [CaseAppealsTableColumnKeys.NEXT_FOLLOW_UP]: "",
  };
};
